import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getRecording, audioUrl, rescoreRecording, retranscribeRecording } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ScoreRing from "@/components/ScoreRing";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, RotateCw, CheckCircle2, XCircle, AlertCircle, MessageSquare, Sparkles,
  TrendingDown, ShieldCheck, ShieldAlert, ShieldOff, Zap, Target, Mic, Wand2,
} from "lucide-react";
import { toast } from "sonner";

function fmt(t) {
  if (t == null || isNaN(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function RecordingDetail() {
  const { id } = useParams();
  const [rec, setRec] = useState(null);
  const [now, setNow] = useState(0);
  const audioRef = useRef(null);
  const activeRef = useRef(null);

  const fetchRec = () => getRecording(id).then(setRec).catch(() => setRec(null));

  useEffect(() => {
    fetchRec();
    const t = setInterval(() => {
      setRec((cur) => {
        if (!cur || ["done", "error"].includes(cur.status)) return cur;
        fetchRec();
        return cur;
      });
    }, 3000);
    return () => clearInterval(t);
  }, [id]);

  const segments = rec?.segments || [];
  const activeIdx = useMemo(() => {
    for (let i = 0; i < segments.length; i++) {
      if (now >= segments[i].start && now <= segments[i].end) return i;
    }
    // otherwise find last one already passed
    for (let i = segments.length - 1; i >= 0; i--) {
      if (now >= segments[i].start) return i;
    }
    return -1;
  }, [now, segments]);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeIdx]);

  if (!rec) return <div className="text-sm text-zinc-500">Loading…</div>;

  const isDone = rec.status === "done";
  const isErr = rec.status === "error";
  const isTranscribed = rec.status === "transcribed";
  const isProcessing = ["pending", "transcribing", "queued_rating", "scoring"].includes(rec.status);
  const hasTranscript = !!(rec.transcript && rec.transcript.length > 0);

  const rescore = async () => {
    try {
      const r = await rescoreRecording(id);
      toast.success(r.step === "score" ? "Re-scoring…" : "Re-transcribing + scoring…");
      fetchRec();
    } catch {
      toast.error("Could not re-score");
    }
  };
  const retranscribe = async () => {
    try {
      await retranscribeRecording(id);
      toast.success("Re-transcribing (this will re-score after)…");
      fetchRec();
    } catch {
      toast.error("Could not re-transcribe");
    }
  };

  const seek = (t) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, t);
    a.play().catch(() => {});
  };

  return (
    <div className="space-y-6" data-testid="recording-detail">
      <Link to="/recordings" className="text-xs text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1.5" data-testid="back-to-recordings">
        <ArrowLeft className="h-3 w-3" /> All recordings
      </Link>

      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="eyebrow">{rec.script_title}</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter mt-1">{rec.title}</h1>
          <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500 flex-wrap">
            <StatusBadge status={rec.status} />
            <span>·</span>
            <span>{new Date(rec.created_at).toLocaleString()}</span>
            {rec.user_name && (<><span>·</span><span>by {rec.user_name}</span></>)}
            {rec.lead_name && (<><span>·</span><span className="text-blue-300">Lead: {rec.lead_name}</span></>)}
            <span>·</span>
            <span className="truncate max-w-[16rem]">{rec.filename}</span>
            {rec.duration_seconds > 0 && (<><span>·</span><span>{fmt(rec.duration_seconds)}</span></>)}
          </div>
        </div>
        {(isDone || isErr || isTranscribed) && (
          <div className="flex items-center gap-2 flex-wrap">
            {hasTranscript && !isDone && (
              <Button
                onClick={rescore}
                className="bg-blue-600 hover:bg-blue-500 rounded-full"
                data-testid="score-now-btn"
              >
                <Wand2 className="h-3.5 w-3.5 mr-2" /> Score now
              </Button>
            )}
            <Button
              variant="outline"
              onClick={retranscribe}
              className="border-[#262626] bg-transparent hover:bg-[#141414]"
              data-testid="retranscribe-btn"
            >
              <Mic className="h-3.5 w-3.5 mr-2" /> Re-transcribe
            </Button>
            {(isDone || (hasTranscript && isErr)) && (
              <Button
                variant="outline"
                onClick={rescore}
                className="border-[#262626] bg-transparent hover:bg-[#141414]"
                data-testid="rescore-btn"
              >
                <RotateCw className="h-3.5 w-3.5 mr-2" /> Re-score
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Pipeline step indicator */}
      <div className="flex items-center gap-2 text-xs" data-testid="pipeline-steps">
        <StepChip label="Transcribe" active={rec.status === "transcribing"} done={hasTranscript} icon={Mic} />
        <div className="h-px flex-1 bg-[#1f1f1f]" />
        <StepChip label="Score" active={rec.status === "scoring"} done={isDone} icon={Wand2} failed={isErr && hasTranscript} />
      </div>

      <div className="sticky top-[73px] z-10 glass border border-[#1f1f1f] rounded-xl p-4">
        <audio
          ref={audioRef}
          controls
          src={audioUrl(id)}
          className="w-full"
          data-testid="audio-player"
          onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
          onSeeked={(e) => setNow(e.currentTarget.currentTime)}
        />
      </div>

      {isErr && (
        <div className="card-surface p-5 border-rose-500/30 bg-rose-500/5" data-testid="error-panel">
          <div className="font-semibold mb-1 text-rose-200">
            {hasTranscript ? "Scoring failed" : "Processing failed"}
          </div>
          <div className="text-rose-300/80 text-sm">{rec.error || "Unknown error"}</div>
          {hasTranscript && (
            <div className="text-xs text-zinc-400 mt-3">
              Good news: your transcript is safely saved below. Hit <b>Score now</b> once you top up the LLM Key balance — no need to re-transcribe.
            </div>
          )}
        </div>
      )}

      {isTranscribed && (
        <div className="card-surface p-5 border-cyan-500/30 bg-cyan-500/5" data-testid="ready-to-score">
          <div className="font-semibold mb-1 text-cyan-200">Transcript ready</div>
          <div className="text-cyan-300/80 text-sm">
            The call has been transcribed but not scored yet. Click <b>Score now</b> to run the Claude Sonnet 5 evaluation.
          </div>
        </div>
      )}

      {isProcessing && (
        <div className="card-surface p-10 text-center">
          <Sparkles className="h-8 w-8 text-blue-500/60 mx-auto animate-pulse" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-bold mt-4">
            {rec.status === "transcribing"
              ? "Transcribing your call…"
              : rec.status === "scoring"
              ? "Scoring against your script…"
              : "Queued for processing…"}
          </h3>
          <p className="text-sm text-zinc-500 mt-1">Long calls are split into chunks automatically. This page updates itself.</p>
        </div>
      )}

      {/* When we have a transcript but haven't scored (or scoring failed with transcript preserved), show it prominently */}
      {hasTranscript && !isDone && (
        <div className="card-surface p-6" data-testid="transcript-standalone">
          <div className="flex items-center justify-between">
            <div>
              <div className="eyebrow">Transcript</div>
              <h3 className="font-display text-lg font-bold mt-1">Full call · click any line to jump</h3>
            </div>
            <div className="text-xs text-zinc-500 font-mono">{fmt(now)}</div>
          </div>
          <div className="mt-4 max-h-[520px] overflow-y-auto pr-2">
            {segments.length === 0 ? (
              <div className="whitespace-pre-wrap text-sm text-zinc-300 leading-relaxed font-mono text-[13px]">
                {rec.transcript}
              </div>
            ) : (
              <ul className="space-y-1.5">
                {segments.map((s, i) => {
                  const active = i === activeIdx;
                  return (
                    <li
                      key={`seg-${s.start ?? i}`}
                      ref={active ? activeRef : null}
                      role="button"
                      tabIndex={0}
                      onClick={() => seek(s.start)}
                      onKeyDown={(e) => e.key === "Enter" && seek(s.start)}
                      data-testid={`segment-${i}`}
                      className={`flex gap-3 rounded-lg px-3 py-2 cursor-pointer transition-colors ${
                        active
                          ? "bg-blue-500/10 border border-blue-500/30"
                          : "hover:bg-[#161616] border border-transparent"
                      }`}
                    >
                      <span className={`font-mono text-[11px] shrink-0 pt-0.5 tabular-nums w-12 ${active ? "text-blue-300" : "text-zinc-500"}`}>
                        {fmt(s.start)}
                      </span>
                      <span className={`text-sm leading-relaxed ${active ? "text-zinc-100" : "text-zinc-300"}`}>
                        {s.text}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {isDone && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-6">
            <div className="card-surface p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
                <ScoreRing value={rec.overall_score} size={140} stroke={11} label="Overall" />
                <MiniMetric label="Adherence" value={`${rec.adherence_pct}%`} tint="emerald" />
                <MiniMetric label="On topic" value={`${rec.on_topic_pct ?? "—"}%`} tint="blue" />
                <MiniMetric label="Objections" value={`${rec.objection_handling_score ?? "—"}`} tint="amber" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 pt-5 border-t border-[#1f1f1f]">
                <Chip label="Tone" value={rec.tone_score} />
                <Chip label="Completeness" value={rec.completeness_score} />
                <Chip label="Recovery" value={rec.avg_recovery_seconds != null ? `${Math.round(rec.avg_recovery_seconds)}s` : "—"} sub="avg seconds to get back" />
                <Chip label="Drift moments" value={(rec.drift_moments || []).length} />
              </div>
              {rec.summary && (
                <>
                  <div className="eyebrow mt-6">Summary</div>
                  <p className="text-sm text-zinc-300 mt-2 leading-relaxed" data-testid="summary-text">{rec.summary}</p>
                </>
              )}
            </div>

            <div className="card-surface p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="eyebrow">Transcript</div>
                  <h3 className="font-display text-lg font-bold mt-1">Click any line to jump the audio</h3>
                </div>
                <div className="text-xs text-zinc-500 font-mono">{fmt(now)}</div>
              </div>
              <div className="mt-4 max-h-[520px] overflow-y-auto pr-2" data-testid="transcript-list">
                {segments.length === 0 ? (
                  <div className="whitespace-pre-wrap text-sm text-zinc-300 leading-relaxed font-mono text-[13px]">
                    {rec.transcript || "No transcript available."}
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {segments.map((s, i) => {
                      const active = i === activeIdx;
                      return (
                        <li
                          key={`kseg-${s.start ?? i}`}
                          ref={active ? activeRef : null}
                          role="button"
                          tabIndex={0}
                          onClick={() => seek(s.start)}
                          onKeyDown={(e) => e.key === "Enter" && seek(s.start)}
                          data-testid={`segment-${i}`}
                          className={`flex gap-3 rounded-lg px-3 py-2 cursor-pointer transition-colors ${
                            active
                              ? "bg-blue-500/10 border border-blue-500/30"
                              : "hover:bg-[#161616] border border-transparent"
                          }`}
                        >
                          <span className={`font-mono text-[11px] shrink-0 pt-0.5 tabular-nums w-12 ${active ? "text-blue-300" : "text-zinc-500"}`}>
                            {fmt(s.start)}
                          </span>
                          <span className={`text-sm leading-relaxed ${active ? "text-zinc-100" : "text-zinc-300"}`}>
                            {s.text}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            {rec.objections?.length > 0 && (
              <div className="card-surface p-6" data-testid="objections-card">
                <div className="eyebrow flex items-center gap-2"><Target className="h-3 w-3" />Objection handling</div>
                <h3 className="font-display text-lg font-bold mt-1 mb-4">How each objection was handled</h3>
                <ul className="space-y-2.5">
                  {(rec.objections || []).map((o, i) => <ObjectionRow key={`obj-${o.start ?? o.text ?? i}`} obj={o} onSeek={seek} />)}
                </ul>
              </div>
            )}

            {(rec.drift_moments?.length > 0 || rec.recovery_events?.length > 0) && (
              <div className="card-surface p-6" data-testid="drift-card">
                <div className="eyebrow flex items-center gap-2"><TrendingDown className="h-3 w-3" />Topic drift</div>
                <h3 className="font-display text-lg font-bold mt-1 mb-4">When the rep went off-script</h3>
                {rec.drift_moments?.length > 0 ? (
                  <ul className="space-y-2">
                    {(rec.drift_moments || []).map((d, i) => {
                      const rec_ev = rec.recovery_events?.[i];
                      return (
                        <li key={`drift-${d.start ?? i}`} className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                          <div className="flex items-center gap-3 justify-between">
                            <button
                              onClick={() => seek(d.start)}
                              className="text-xs font-mono text-amber-300 hover:text-amber-200"
                              data-testid={`drift-jump-${i}`}
                            >
                              {fmt(d.start)} → {fmt(d.end)}
                            </button>
                            {rec_ev?.duration_seconds != null && (
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-emerald-300">
                                <Zap className="h-3 w-3" />
                                pulled back in {Math.round(rec_ev.duration_seconds)}s
                              </span>
                            )}
                          </div>
                          {d.text && <div className="text-xs text-zinc-300 mt-1.5 italic">&quot;{d.text}&quot;</div>}
                          {d.note && <div className="text-xs text-zinc-500 mt-1">{d.note}</div>}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="text-sm text-emerald-300 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" /> Rep stayed on script throughout
                  </div>
                )}
              </div>
            )}

            {(rec.part_scores?.length > 0 || rec.discovery_depth) && (
              <div className="card-surface p-6" data-testid="part-scores">
                <div className="eyebrow">Script parts</div>
                <h3 className="font-display text-lg font-bold mt-1 mb-4">Score by part</h3>
                {rec.discovery_depth && (
                  <div className="mb-4 rounded-lg border border-[#262626] bg-[#0a0a0a] p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-zinc-300">Discovery depth</span>
                      <span className={`rounded-full border px-2.5 py-0.5 text-xs ${DEPTH[rec.discovery_depth]?.cls || "border-[#333] text-zinc-400"}`}>
                        {DEPTH[rec.discovery_depth]?.label || rec.discovery_depth}
                      </span>
                    </div>
                    {rec.discovery_depth_note && <p className="text-xs text-zinc-500 mt-1.5">{rec.discovery_depth_note}</p>}
                  </div>
                )}
                <ul className="space-y-3">
                  {(rec.part_scores || []).map((p, i) => (
                    <li key={`${p.part}-${i}`}>
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="text-zinc-200">{p.part}</span>
                        <span className={`font-display font-bold tabular-nums ${p.score >= 75 ? "text-emerald-300" : p.score >= 50 ? "text-amber-300" : "text-rose-300"}`}>{p.score}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-[#1a1a1a] overflow-hidden">
                        <div className={`h-full rounded-full ${p.score >= 75 ? "bg-emerald-500" : p.score >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${p.score}%` }} />
                      </div>
                      {p.verdict && <p className="text-xs text-zinc-500 mt-1">{p.verdict}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="card-surface p-6">
              <div className="eyebrow">Script coverage</div>
              <h3 className="font-display text-lg font-bold mt-1 mb-4">Section-by-section</h3>
              <ul className="space-y-2" data-testid="sections-list">
                {rec.sections?.map((s) => <SectionRow key={s.name || s.title || `sec-${s.start ?? Math.random()}`} section={s} />)}
                {(!rec.sections || rec.sections.length === 0) && (
                  <li className="text-sm text-zinc-500">No sections returned.</li>
                )}
              </ul>
            </div>

            {rec.coaching_feedback && (
              <div className="card-surface p-6">
                <div className="eyebrow flex items-center gap-2"><MessageSquare className="h-3 w-3" />Coaching</div>
                <h3 className="font-display text-lg font-bold mt-1 mb-3">What to work on</h3>
                <p className="text-sm text-zinc-300 leading-relaxed" data-testid="coaching-text">{rec.coaching_feedback}</p>
              </div>
            )}

            {rec.strengths?.length > 0 && (
              <ListCard title="Strengths" items={rec.strengths} tint="emerald" testid="strengths-list" />
            )}
            {rec.missed_points?.length > 0 && (
              <ListCard title="Missed points" items={rec.missed_points} tint="rose" testid="missed-list" />
            )}
            {rec.off_script_points?.length > 0 && (
              <ListCard title="Off-script content" items={rec.off_script_points} tint="amber" testid="offscript-list" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniMetric({ label, value, tint }) {
  const tints = {
    emerald: "text-emerald-400",
    blue: "text-blue-400",
    amber: "text-amber-400",
  };
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className={`font-display text-3xl font-extrabold mt-2 ${tints[tint]}`}>{value}</div>
    </div>
  );
}

const DEPTH = {
  not_asked: { label: "Didn't ask why", cls: "border-rose-500/40 bg-rose-500/10 text-rose-300" },
  surface: { label: "Surface only", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  one_level_deeper: { label: "One level deeper", cls: "border-blue-500/40 bg-blue-500/10 text-blue-300" },
  real_pain: { label: "Real pain reached", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
};

function SectionRow({ section }) {
  const cfg = {
    hit: { icon: CheckCircle2, cls: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" },
    missed: { icon: XCircle, cls: "text-rose-400 bg-rose-400/10 border-rose-400/20" },
    paraphrased: { icon: AlertCircle, cls: "text-amber-400 bg-amber-400/10 border-amber-400/20" },
  }[section.status] || { icon: AlertCircle, cls: "text-zinc-400 bg-zinc-400/5 border-zinc-500/20" };
  const Icon = cfg.icon;
  return (
    <li className="rounded-lg border border-[#1f1f1f] p-3 hover:bg-[#161616] transition-colors">
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full border ${cfg.cls} shrink-0`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <div className="font-medium text-sm">{section.name}</div>
            <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${cfg.cls.split(" ")[0]}`}>{section.status}</span>
          </div>
          {section.evidence && (
            <div className="text-xs text-zinc-400 mt-1.5 italic border-l-2 border-[#262626] pl-2.5">&quot;{section.evidence}&quot;</div>
          )}
          {section.note && <div className="text-xs text-zinc-500 mt-1.5">{section.note}</div>}
        </div>
      </div>
    </li>
  );
}

function ListCard({ title, items, tint, testid }) {
  const dot = { emerald: "bg-emerald-400", rose: "bg-rose-400", amber: "bg-amber-400" }[tint];
  return (
    <div className="card-surface p-6" data-testid={testid}>
      <div className="eyebrow">{title}</div>
      <ul className="mt-3 space-y-2">
        {(items || []).map((it, i) => (
          <li key={`tip-${typeof it === "string" ? it.slice(0, 24) : i}`} className="text-sm text-zinc-300 flex gap-2.5 leading-relaxed">
            <span className={`h-1.5 w-1.5 rounded-full ${dot} mt-2 shrink-0`} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Chip({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-[#1f1f1f] bg-[#0d0d0d] px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="font-display text-xl font-extrabold mt-1 tabular-nums">{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function StepChip({ label, active, done, failed, icon: Icon }) {
  let tint = "text-zinc-500 border-[#1f1f1f] bg-[#0d0d0d]";
  if (done && !failed) tint = "text-emerald-300 border-emerald-500/20 bg-emerald-500/5";
  if (active) tint = "text-blue-300 border-blue-500/30 bg-blue-500/10";
  if (failed) tint = "text-rose-300 border-rose-500/30 bg-rose-500/5";
  return (
    <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-medium ${tint}`}>
      <Icon className={`h-3 w-3 ${active ? "animate-pulse" : ""}`} />
      {label}
      {done && !failed && <CheckCircle2 className="h-3 w-3" />}
      {failed && <XCircle className="h-3 w-3" />}
    </div>
  );
}

function ObjectionRow({ obj, onSeek }) {
  const cfg = {
    overcome: { icon: ShieldCheck, cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", label: "Overcome" },
    agreed_to: { icon: ShieldAlert, cls: "text-rose-400 bg-rose-500/10 border-rose-500/20", label: "Agreed to" },
    deflected: { icon: ShieldAlert, cls: "text-amber-400 bg-amber-500/10 border-amber-500/20", label: "Deflected" },
    unaddressed: { icon: ShieldOff, cls: "text-rose-400 bg-rose-500/10 border-rose-500/20", label: "Unaddressed" },
  }[obj.handling] || { icon: ShieldAlert, cls: "text-zinc-400 bg-zinc-500/10 border-zinc-500/20", label: obj.handling };
  const Icon = cfg.icon;
  return (
    <li className="rounded-lg border border-[#1f1f1f] p-3 hover:bg-[#161616] transition-colors">
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full border ${cfg.cls} shrink-0`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <div className="font-medium text-sm">{obj.objection}</div>
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${cfg.cls.split(" ")[0]}`}>{cfg.label}</span>
              {obj.at_time != null && (
                <button data-testid="recording-detail-button-512"
                  onClick={() => onSeek(obj.at_time)}
                  className="text-[10px] font-mono text-blue-300 hover:text-blue-200"
                >
                  {fmt(obj.at_time)}
                </button>
              )}
            </div>
          </div>
          {obj.how_handled && <div className="text-xs text-zinc-400 mt-1.5">{obj.how_handled}</div>}
          {obj.quote && <div className="text-xs text-zinc-500 mt-1.5 italic border-l-2 border-[#262626] pl-2.5">&quot;{obj.quote}&quot;</div>}
        </div>
      </div>
    </li>
  );
}
